/**
 * Insight Edge - Main JavaScript
 */

// Import styles (for Vite)
import '../scss/style.scss';

// ============================================
// DOM Ready
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileMenu();
  initNavDropdown();
  initBackToTop();
  initMemberModal();
  initTeamModal();
  initAnimations();
  initNewsCategoryFilter();
  initHeroAnimation();
  initMissionAnimation();
  initProjectsAnimation();
  initProjectRelatedCarousel();
  initNewsRelatedCarousel();
  initBusinessCarousels();
  initBusinessPageAnimation();
  initCompanyPageAnimation();
  initNewsAnimation();
  initBlogAnimation();
  initRecruitAnimation();
  initCareersPageAnimation();
  initTeamPageAnimation();
  initNewsPageAnimation();
  initProjectsPageAnimation();
  initInterviewPageAnimation();
  initPolicyPageAnimation();
  initContactForm();
});

// ============================================
// Header Scroll Effect
// ============================================
function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const checkScroll = () => {
    if (window.scrollY > 50) {
      header.classList.add('is-scrolled');
    } else {
      header.classList.remove('is-scrolled');
    }
  };

  window.addEventListener('scroll', checkScroll, { passive: true });
  checkScroll();
}

// ============================================
// Mobile Menu
// ============================================
function initMobileMenu() {
  const toggle = document.querySelector('.menu-toggle');
  const mobileNav = document.getElementById('mobile-navigation');
  const header = document.querySelector('.site-header');

  if (!toggle || !mobileNav) return;

  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    // 一度でも開いたらwas-openedクラスを追加（閉じるアニメーション用）
    if (!isOpen) {
      toggle.classList.add('was-opened');
    }

    toggle.setAttribute('aria-expanded', !isOpen);
    mobileNav.classList.toggle('is-open');
    document.body.classList.toggle('menu-open');
    if (header) header.classList.toggle('menu-open');
  });

  // Close menu when clicking a link
  const menuLinks = mobileNav.querySelectorAll('a');
  menuLinks.forEach(link => {
    link.addEventListener('click', () => {
      toggle.setAttribute('aria-expanded', 'false');
      mobileNav.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      if (header) header.classList.remove('menu-open');
    });
  });
}

// ============================================
// Navigation Dropdown (Company menu)
// ============================================
function initNavDropdown() {
  const toggles = document.querySelectorAll('.nav-dropdown-toggle');

  toggles.forEach((toggle) => {
    const dropdownId = toggle.getAttribute('aria-controls');
    const dropdown = dropdownId ? document.getElementById(dropdownId) : null;
    const menuItem = toggle.closest('.has-dropdown');

    if (!dropdown) return;

    let closeTimeout = null;

    // ドロップダウンを開く
    function openDropdown() {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
      dropdown.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      positionDropdown(toggle, dropdown);
    }

    // ドロップダウンを閉じる（遅延付き）
    function closeDropdown() {
      closeTimeout = setTimeout(() => {
        dropdown.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      }, 100); // 100msの遅延でスムーズな移行を可能に
    }

    // メニューアイテムのホバー
    menuItem.addEventListener('mouseenter', openDropdown);
    menuItem.addEventListener('mouseleave', closeDropdown);

    // ドロップダウン自体のホバー（メニューから移動してきた時に閉じないようにする）
    dropdown.addEventListener('mouseenter', openDropdown);
    dropdown.addEventListener('mouseleave', closeDropdown);

    // Companyはリンクとして機能（クリックでページ遷移）
  });

  // ドロップダウンの位置を調整（Companyの中央にTeamの中央を揃える）
  function positionDropdown(toggle, dropdown) {
    const toggleRect = toggle.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();
    const header = document.querySelector('.site-header');
    const headerRect = header ? header.getBoundingClientRect() : { left: 0 };

    // Companyの中央位置
    const toggleCenter = toggleRect.left + toggleRect.width / 2;
    // ドロップダウンの幅の半分を引いて、中央揃えの左位置を計算
    const dropdownHalfWidth = dropdownRect.width / 2;
    const left = toggleCenter - headerRect.left - dropdownHalfWidth;

    dropdown.style.left = `${left}px`;
  }

  // ESCキーで閉じる
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.nav-dropdown').forEach((d) => {
        d.classList.remove('is-open');
      });
      document.querySelectorAll('.nav-dropdown-toggle').forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });
    }
  });
}

// ============================================
// Back to Top Button
// ============================================
function initBackToTop() {
  const button = document.querySelector('.back-to-top');
  if (!button) return;

  button.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
}

// ============================================
// Member Modal
// ============================================
function initMemberModal() {
  const modal = document.getElementById('memberModal');
  if (!modal) return;

  const memberCards = document.querySelectorAll('.member-card');
  const closeButtons = modal.querySelectorAll('[data-modal-close]');

  memberCards.forEach(card => {
    const button = card.querySelector('.member-card__button');
    if (!button) return;

    button.addEventListener('click', () => {
      const data = {
        name: card.dataset.memberName,
        position: card.dataset.memberPosition,
        profile: card.dataset.memberProfile,
        career: card.dataset.memberCareer,
        message: card.dataset.memberMessage,
        photo: card.dataset.memberPhoto,
      };

      openMemberModal(modal, data);
    });
  });

  closeButtons.forEach(btn => {
    btn.addEventListener('click', () => closeMemberModal(modal));
  });

  // ESCキーで閉じる
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeMemberModal(modal);
    }
  });
}

function openMemberModal(modal, data) {
  const photoEl = modal.querySelector('.member-modal__photo');
  const nameEl = modal.querySelector('.member-modal__name');
  const positionEl = modal.querySelector('.member-modal__position');
  const profileEl = modal.querySelector('.member-modal__profile');
  const careerEl = modal.querySelector('.member-modal__career');
  const messageEl = modal.querySelector('.member-modal__message');

  if (photoEl && data.photo) {
    photoEl.innerHTML = `<img src="${data.photo}" alt="${data.name}">`;
  }
  if (nameEl) nameEl.textContent = data.name || '';
  if (positionEl) positionEl.textContent = data.position || '';
  if (profileEl) profileEl.innerHTML = `<p>${data.profile || ''}</p>`;
  if (careerEl) {
    const careerContent = careerEl.querySelector('h3');
    if (careerContent && careerContent.nextElementSibling) {
      careerContent.nextElementSibling.remove();
    }
    if (data.career) {
      careerEl.insertAdjacentHTML('beforeend', `<p>${data.career}</p>`);
    }
  }
  if (messageEl) {
    const messageContent = messageEl.querySelector('h3');
    if (messageContent && messageContent.nextElementSibling) {
      messageContent.nextElementSibling.remove();
    }
    if (data.message) {
      messageEl.insertAdjacentHTML('beforeend', `<p>${data.message}</p>`);
    }
  }

  modal.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
}

function closeMemberModal(modal) {
  modal.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

// ============================================
// Scroll Animations (GSAP)
// ============================================
function initAnimations() {
  // GSAPがない場合はスキップ
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.log('GSAP not loaded, skipping animations');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  // Fade up animation
  gsap.utils.toArray('[data-animate="fade-up"]').forEach(el => {
    gsap.from(el, {
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        toggleActions: 'play none none none',
      },
      y: 30,
      opacity: 0,
      duration: 0.8,
      delay: parseFloat(el.dataset.delay) || 0,
      ease: 'power2.out',
    });
  });

  // Stagger animation
  gsap.utils.toArray('[data-animate="stagger"]').forEach(container => {
    const items = container.querySelectorAll('[data-animate-item]');
    if (items.length === 0) return;

    gsap.from(items, {
      scrollTrigger: {
        trigger: container,
        start: 'top 85%',
        toggleActions: 'play none none none',
      },
      y: 30,
      opacity: 0,
      duration: 0.6,
      stagger: 0.1,
      ease: 'power2.out',
    });
  });

  // Rotating logo
  const rotatingLogo = document.querySelector('.rotating-logo');
  if (rotatingLogo) {
    gsap.to(rotatingLogo, {
      rotation: 360,
      duration: 60,
      repeat: -1,
      ease: 'none',
    });
  }
}

// ============================================
// News Category Filter (AJAX-based filtering)
// ============================================
function initNewsCategoryFilter() {
  const categoryFilter = document.getElementById('category-filter');
  const newsMain = document.getElementById('news-main');

  if (!categoryFilter || !newsMain || typeof insightEdgeAjax === 'undefined') return;

  // 最初のアクティブなボタンからカテゴリを取得
  const activeButton = categoryFilter.querySelector('.category-filter__item.is-active');
  let currentCategory = activeButton ? activeButton.dataset.category : 'information-ja';
  let isLoading = false;

  // ベースURL（/page/N/ を除去した素のニュースURL）
  const baseUrl = window.location.pathname.replace(/\/page\/\d+\/?$/, '/');

  // URLからカテゴリとページ番号を復元（クエリパラメータ + パス両方対応）
  const urlParams = new URLSearchParams(window.location.search);
  const initialCategory = urlParams.get('category');
  const pathPageMatch = window.location.pathname.match(/\/page\/(\d+)/);
  const initialPage = urlParams.get('paged') || (pathPageMatch ? pathPageMatch[1] : null);

  if (initialCategory) {
    currentCategory = initialCategory;
    // アクティブなカテゴリボタンを更新
    categoryFilter.querySelectorAll('.category-filter__item').forEach(btn => {
      btn.classList.remove('is-active');
      if (btn.dataset.category === initialCategory) {
        btn.classList.add('is-active');
      }
    });
  }

  // 初期表示（URLにパラメータがある場合はその状態を復元）
  if (initialCategory || initialPage) {
    fetchNews(currentCategory, initialPage || 1, false);
  }

  // 現在の状態をURLに反映（常にクリーンなクエリパラメータ形式を使用）
  function updateURL(category, paged) {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('category', category);
    if (paged > 1) {
      url.searchParams.set('paged', paged);
    }
    history.pushState({ category, paged: Number(paged) }, '', url);
  }

  // ブラウザバック/フォワード対応
  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.category) {
      currentCategory = e.state.category;
      // カテゴリボタンのアクティブ状態を更新
      categoryFilter.querySelectorAll('.category-filter__item').forEach(btn => {
        btn.classList.remove('is-active');
        if (btn.dataset.category === e.state.category) {
          btn.classList.add('is-active');
        }
      });
      fetchNews(e.state.category, e.state.paged || 1, false);
    } else {
      // 初期状態に戻る
      const firstBtn = categoryFilter.querySelector('.category-filter__item');
      if (firstBtn) {
        currentCategory = firstBtn.dataset.category;
        categoryFilter.querySelectorAll('.category-filter__item').forEach(btn => {
          btn.classList.remove('is-active');
        });
        firstBtn.classList.add('is-active');
        fetchNews(currentCategory, 1, false);
      }
    }
  });

  // カテゴリボタンのクリック
  categoryFilter.addEventListener('click', (e) => {
    const button = e.target.closest('.category-filter__item');
    if (!button || isLoading) return;

    const category = button.dataset.category;
    if (category === currentCategory) return;

    currentCategory = category;

    // Update active state
    categoryFilter.querySelectorAll('.category-filter__item').forEach(btn => {
      btn.classList.remove('is-active');
    });
    button.classList.add('is-active');

    // Fetch posts
    fetchNews(category, 1, true);
  });

  // ページネーションのクリック（イベント委譲）
  newsMain.addEventListener('click', (e) => {
    const link = e.target.closest('.pagination__item a');
    if (!link || isLoading) return;

    e.preventDefault();

    // URLからページ番号を取得（両方のフォーマットに対応）
    const url = new URL(link.href);
    let paged = url.searchParams.get('paged');

    // /page/2/ 形式の場合
    if (!paged) {
      const pageMatch = url.pathname.match(/\/page\/(\d+)/);
      paged = pageMatch ? pageMatch[1] : 1;
    }

    fetchNews(currentCategory, paged, true);
  });

  // AJAX fetch
  function fetchNews(category, paged, pushHistory) {
    isLoading = true;
    newsMain.classList.add('is-loading');

    if (pushHistory) {
      updateURL(category, paged);
    }

    const formData = new FormData();
    formData.append('action', 'filter_news');
    formData.append('category', category);
    formData.append('paged', paged);

    fetch(insightEdgeAjax.ajaxUrl, {
      method: 'POST',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          newsMain.innerHTML = data.data.html;

          // 新しく読み込まれたカードにis-visibleクラスを追加（アニメーション用）
          const newCards = newsMain.querySelectorAll('.news-card');
          newCards.forEach((card, index) => {
            setTimeout(() => {
              card.classList.add('is-visible');
            }, index * 50);
          });

          // ページネーションにもis-visibleを追加
          const pagination = newsMain.querySelector('.pagination');
          if (pagination) {
            pagination.classList.add('is-visible');
          }

          // ページ最上部にスクロール（ブラウザバック以外）
          if (pushHistory) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else {
          console.error('AJAX error:', data);
        }
      })
      .catch(error => {
        console.error('News filter error:', error);
      })
      .finally(() => {
        isLoading = false;
        newsMain.classList.remove('is-loading');
      });
  }
}

// ============================================
// Hero Mask Animation
// ============================================
function initHeroAnimation() {
  const heroMask = document.querySelector('.hero__mask');
  const heroCopyEn = document.querySelector('.hero__copy-en');
  const heroCopyJa = document.querySelector('.hero__copy-ja') || document.querySelector('.hero__copy-sub');
  const heroIndicator = document.querySelector('.hero__scroll-indicator');

  if (!heroMask) return;

  // アニメーションのタイムライン
  const timeline = {
    initialDelay: 500,           // 初期遅延
    totalDuration: 1400,         // 全体の時間
    verticalStartAt: 400,        // 縦開始（横が始まってから）
    copyEnDelay: 400,            // 英語コピー表示（展開完了後）
    copyJaDelay: 600,            // 日本語コピー表示（英語の後）
    indicatorDelay: 500,         // インジケーター表示
    headerDelay: 400,            // ヘッダー表示開始（メインコピーと同時）
  };

  // イージング関数
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  // 横と縦を同期してアニメーション（両方JSで制御）
  function animateMask() {
    const startTime = performance.now();
    const totalDuration = timeline.totalDuration;
    const verticalDelay = timeline.verticalStartAt;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;

      // 横: 0ms〜totalDurationで24%→100%
      const hProgress = Math.min(elapsed / totalDuration, 1);
      const hEased = easeOutCubic(hProgress);
      const width = 24 + (76 * hEased);
      heroMask.style.setProperty('--mask-width', `${width}%`);

      // 縦: verticalDelay後から開始、totalDurationで終了
      if (elapsed > verticalDelay) {
        const vElapsed = elapsed - verticalDelay;
        const vDuration = totalDuration - verticalDelay;
        const vProgress = Math.min(vElapsed / vDuration, 1);
        const vEased = easeOutCubic(vProgress);
        const height = 7 + (93 * vEased);
        heroMask.style.setProperty('--mask-height', `${height}%`);
      }

      if (elapsed < totalDuration) {
        requestAnimationFrame(animate);
      } else {
        // 完了
        heroMask.style.removeProperty('--mask-width');
        heroMask.style.removeProperty('--mask-height');
        heroMask.classList.add('is-expanded');
        onExpandComplete();
      }
    };

    requestAnimationFrame(animate);
  }

  function onExpandComplete() {
    // 英語コピーをスライドアップ
    setTimeout(() => {
      if (heroCopyEn) heroCopyEn.classList.add('is-visible');
      setTimeout(() => {
        if (heroCopyJa) heroCopyJa.classList.add('is-visible');
      }, timeline.copyJaDelay - timeline.copyEnDelay);
    }, timeline.copyEnDelay);

    // インジケーター表示
    setTimeout(() => {
      if (heroIndicator) heroIndicator.classList.add('is-visible');
    }, timeline.indicatorDelay);

    // ヘッダー表示
    setTimeout(() => {
      const header = document.querySelector('.site-header');
      if (header && document.body.classList.contains('home')) {
        header.classList.add('is-visible');
      }
    }, timeline.headerDelay);
  }

  // Step 1: 初期遅延後、アニメーション開始
  setTimeout(() => {
    animateMask();
  }, timeline.initialDelay);
}

// ============================================
// Mission Section Animation (Scroll Triggered)
// ============================================
function initMissionAnimation() {
  const mission = document.querySelector('.mission');
  if (!mission) return;

  const missionMask = mission.querySelector('.mission__mask');
  const missionTitle = mission.querySelector('.mission__title');

  if (!missionMask) return;

  // アニメーションのタイムライン
  const timeline = {
    expandDuration: 1200,
    titleDelay: 400,
  };

  let hasAnimated = false;

  // 初期ビューポートにMissionが見えているかチェック
  const rect = mission.getBoundingClientRect();
  const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
  const visibleRatio = rect.height > 0 ? visibleHeight / rect.height : 0;
  const isInInitialViewport = visibleRatio >= 0.3;

  if (isInInitialViewport) {
    // ヒーローと同じ小さいマスクサイズ + opacity:0 で待機
    missionMask.classList.add('is-hero-synced');

    // ヘッダー表示タイミングでopacity 0→1 + 展開開始
    // Hero: initialDelay(500) + totalDuration(1400) + headerDelay(400) = 2300ms
    const headerShowTime = 500 + 1400 + 400;
    const heroTimer = setTimeout(() => {
      if (!hasAnimated) {
        hasAnimated = true;
        startSyncedMissionAnimation();
      }
    }, headerShowTime);

    // スクロールを検知したら待たずに即座に開始
    const onScroll = () => {
      if (!hasAnimated) {
        hasAnimated = true;
        clearTimeout(heroTimer);
        startSyncedMissionAnimation();
      }
      window.removeEventListener('scroll', onScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

  } else {
    // ビューポート外: 即座にopacity:1にして従来通りスクロール検知
    missionMask.classList.add('is-scroll-ready');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasAnimated) {
          hasAnimated = true;
          startMissionAnimation();
        }
      });
    }, {
      threshold: 0.3,
    });

    observer.observe(mission);
  }

  // 初期ビューポート内の場合: ヘッダーと同時にopacity表示 → 展開アニメーション
  function startSyncedMissionAnimation() {
    // opacity:0→1で表示（ヒーロー同サイズのマスク状態で現れる）
    missionMask.classList.add('is-visible');

    // 少し間を置いてから展開開始（表示を認識できる程度）
    setTimeout(() => {
      missionMask.classList.add('is-expanding');

      // 展開完了後
      setTimeout(() => {
        missionMask.classList.remove('is-hero-synced', 'is-visible', 'is-expanding');
        missionMask.classList.add('is-expanded');

        // タイトルをスライドアップ
        setTimeout(() => {
          if (missionTitle) {
            missionTitle.classList.add('is-visible');
          }
        }, timeline.titleDelay);

      }, timeline.expandDuration);
    }, 200);
  }

  // ビューポート外からスクロールで入ってきた場合（従来通り）
  function startMissionAnimation() {
    missionMask.classList.add('is-expanding');

    setTimeout(() => {
      missionMask.classList.remove('is-expanding');
      missionMask.classList.add('is-expanded');

      setTimeout(() => {
        if (missionTitle) {
          missionTitle.classList.add('is-visible');
        }
      }, timeline.titleDelay);

    }, timeline.expandDuration);
  }
}

// ============================================
// Projects Section Animation (Auto-trigger + Carousel)
// ============================================
function initProjectsAnimation() {
  // トップページのprojectsセクションのみを対象（詳細ページの関連カルーセルは除外）
  const projects = document.querySelector('.projects:not(.project-single__related-carousel):not(.news-single__related-carousel)');
  if (!projects) return;

  const initialImage = projects.querySelector('.projects__initial-image');
  const content = projects.querySelector('.projects__content');
  const title = projects.querySelector('.projects__title');
  const cards = projects.querySelectorAll('.projects__card');
  const controls = projects.querySelector('.projects__controls');
  const carouselTrack = projects.querySelector('.projects__carousel-track');
  const prevBtn = projects.querySelector('.projects__nav-btn--prev');
  const nextBtn = projects.querySelector('.projects__nav-btn--next');

  // カルーセル設定
  const visibleCards = 3;
  let currentIndex = 0;
  const totalCards = cards.length;

  // デスクトップかどうかを判定
  const isDesktop = () => window.innerWidth >= 960;

  // 自動トリガーアニメーション用の状態
  let animationTriggered = false;
  let animationComplete = false;

  // 無限ループカルーセル用の状態
  let isTransitioning = false;
  let clonesBefore = [];
  let clonesAfter = [];
  let initialImageHidden = false;

  // 初期画像を非表示にして最初のカードのサムネイルを表示（即座に入れ替え）
  function hideInitialImage() {
    if (initialImageHidden) return;
    initialImageHidden = true;

    // carousel-startedクラスを追加してCSSルールを無効化（すべてのサムネイルが表示される）
    projects.classList.add('carousel-started');

    // 初期画像を非表示
    if (initialImage) {
      initialImage.classList.add('is-hidden');
    }
  }

  // 画像の初期状態と最終状態を計算
  function getImageStates() {
    const viewportWidth = window.innerWidth;
    const isLarge = viewportWidth >= 1800;

    // 初期状態（大きい画像）
    const initial = {
      width: viewportWidth - 40,
      height: Math.min(1000, viewportWidth * 1000 / 1920),
      left: 20,
      top: (projects.offsetHeight / 2) - (Math.min(1000, viewportWidth * 1000 / 1920) / 2),
      borderRadius: Math.min(20, viewportWidth * 20 / 1920),
    };

    // カルーセル幅: min(1880px, vw-desktop(1880)) - CSSと同じ計算
    const carouselWidth = Math.min(1880, viewportWidth * 1880 / 1920);

    // カルーセル左マージン（margin: 0 autoで中央寄せ）
    const carouselLeft = (viewportWidth - carouselWidth) / 2;

    // Gap合計: 1800px以上は80px固定、未満はvw-desktop(80)
    const gapTotal = isLarge ? 80 : viewportWidth * 80 / 1920;

    // カード幅: (カルーセル幅 - gap) / 3
    let finalWidth = (carouselWidth - gapTotal) / 3;
    if (isLarge) {
      finalWidth = Math.min(600, finalWidth); // max-width: 600px
    }

    // 着地値（位置・サイズ）は本物の1枚目カードサムネを実測して完全一致させる（ハードコードは数pxズレる）
    // top: offsetTopチェーン＝レイアウト基準でreveal用transform(translateY)を含まない＝最終静止位置
    // left/幅/高さ: getBoundingClientRect＝カルーセルのtranslateXを含む正しい視覚位置・実寸
    let measuredTop = null, measuredLeft = null, measuredW = null, measuredH = null;
    const firstThumb = projects.querySelector('.projects__card[data-index="1"] .projects__card-thumbnail');
    if (firstThumb) {
      let t = 0, el = firstThumb;
      while (el && el !== projects) { t += el.offsetTop; el = el.offsetParent; }
      if (el === projects) measuredTop = t;
      const tr = firstThumb.getBoundingClientRect();
      const pr = projects.getBoundingClientRect();
      if (tr.width > 0) { measuredLeft = tr.left - pr.left; measuredW = tr.width; measuredH = tr.height; }
    }

    const final = {
      width: measuredW != null ? measuredW : finalWidth,
      height: measuredH != null ? measuredH : finalWidth * 318 / 600,
      left: measuredLeft != null ? measuredLeft : carouselLeft,
      top: measuredTop != null ? measuredTop : Math.min(360, viewportWidth * 360 / 1920),
      borderRadius: Math.min(20, viewportWidth * 20 / 1920),
    };

    return { initial, final };
  }

  // 線形補間
  function lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  // イージング関数（ease-in: 最初ゆっくり、後半ほんのり速い）
  function easeInMild(t) {
    return Math.pow(t, 1.5);
  }

  // 画像のスタイルを更新
  function updateImageStyle(progress) {
    if (!initialImage || !isDesktop()) return;

    const { initial, final } = getImageStates();
    const easedProgress = easeInMild(progress);

    const width = lerp(initial.width, final.width, easedProgress);
    const height = lerp(initial.height, final.height, easedProgress);
    const left = lerp(initial.left, final.left, easedProgress);
    const top = lerp(initial.top, final.top, easedProgress);
    const borderRadius = lerp(initial.borderRadius, final.borderRadius, easedProgress);

    initialImage.style.width = `${width}px`;
    initialImage.style.height = `${height}px`;
    initialImage.style.left = `${left}px`;
    initialImage.style.top = `${top}px`;
    initialImage.style.borderRadius = `${borderRadius}px`;
  }

  // 自動縮小アニメーションを実行
  function runShrinkAnimation() {
    if (animationTriggered) return;
    animationTriggered = true;

    const duration = 1300; // 1.3秒でアニメーション
    const startTime = performance.now();

    // コンテンツを先に表示
    if (content) content.classList.add('is-visible');
    setTimeout(() => {
      if (title) title.classList.add('is-visible');
    }, 100);

    // カルーセルのセットアップ
    if (totalCards > visibleCards) {
      setupInfiniteCarousel();
    }

    function animate(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // 画像を更新
      updateImageStyle(progress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // アニメーション完了
        onShrinkComplete();
      }
    }

    requestAnimationFrame(animate);
  }

  // 縮小アニメーション完了後の処理
  function onShrinkComplete() {
    animationComplete = true;

    // カードを順番に表示
    cards.forEach((card, index) => {
      setTimeout(() => {
        card.classList.add('is-visible');
      }, index * 150);
    });

    // カードのふわっとアニメーション完了後にcarousel-startedを追加
    // 最後のカードの表示開始（(visibleCards-1)*150）+ transitionの完了（500ms）
    setTimeout(() => {
      projects.classList.add('carousel-started');
    }, (visibleCards - 1) * 150 + 500);

    // 初期画像はそのまま残す（着地が実カードに一致するので重なったまま自然。カルーセルをスライドした時に本物サムネへ切替）

    // コントロールを表示
    setTimeout(() => {
      if (controls) controls.classList.add('is-visible');
    }, visibleCards * 150 + 200);
  }

  // 初期化（デスクトップ）
  if (isDesktop() && initialImage) {
    // 初期状態を設定
    updateImageStyle(0);

    // IntersectionObserverでトリガー
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !animationTriggered) {
          observer.unobserve(projects);
          runShrinkAnimation();
        }
      });
    }, {
      threshold: 0.3, // セクションが30%見えたらトリガー
    });

    observer.observe(projects);
  } else {
    // モバイル用アニメーション
    initMobileAnimation();
  }

  // モバイルアニメーション（フェードインアップ）
  function initMobileAnimation() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(projects);
          startMobileAnimation();
        }
      });
    }, { threshold: 0.2 });

    observer.observe(projects);
  }

  function startMobileAnimation() {
    if (content) content.classList.add('is-visible');

    setTimeout(() => {
      if (title) title.classList.add('is-visible');
    }, 100);

    cards.forEach((card, index) => {
      setTimeout(() => {
        card.classList.add('is-visible');
      }, 200 + (index * 100));
    });

    setTimeout(() => {
      if (controls) controls.classList.add('is-visible');
    }, 200 + (cards.length * 100) + 100);
  }

  // 無限ループカルーセル機能（デスクトップのみ）
  function setupInfiniteCarousel() {
    if (!isDesktop() || !carouselTrack || totalCards <= visibleCards) return;

    // 既存のクローンを削除
    clonesBefore.forEach(clone => clone.remove());
    clonesAfter.forEach(clone => clone.remove());
    clonesBefore = [];
    clonesAfter = [];

    // クローン追加前にカード幅とgapを取得
    const cardWidth = getCardWidth();
    const gap = getGap();
    const numClonesBefore = Math.min(visibleCards, totalCards);

    // トランジションを一時的に無効化
    carouselTrack.style.transition = 'none';

    // 最後のvisibleCards枚を先頭にクローン（前に戻る用）
    for (let i = totalCards - numClonesBefore; i < totalCards; i++) {
      const clone = cards[i].cloneNode(true);
      clone.classList.add('is-clone');
      clone.classList.add('is-visible');
      clonesBefore.push(clone);
    }
    // 逆順で挿入
    for (let i = clonesBefore.length - 1; i >= 0; i--) {
      carouselTrack.insertBefore(clonesBefore[i], carouselTrack.firstChild);
    }

    // 最初のvisibleCards枚を末尾にクローン（先に進む用）
    for (let i = 0; i < visibleCards; i++) {
      const clone = cards[i].cloneNode(true);
      clone.classList.add('is-clone');
      clone.classList.add('is-visible');
      carouselTrack.appendChild(clone);
      clonesAfter.push(clone);
    }

    // 初期位置をクローン分ずらす
    currentIndex = clonesBefore.length;
    const offset = currentIndex * (cardWidth + gap);
    carouselTrack.style.transform = `translateX(-${offset}px)`;
  }

  function getCardWidth() {
    const card = cards[0];
    if (!card) return 560;
    return card.offsetWidth;
  }

  function getGap() {
    // CSSのgap値を取得
    if (!carouselTrack) return 40;
    const style = window.getComputedStyle(carouselTrack);
    const gap = parseFloat(style.gap) || 40;
    return gap;
  }

  function updateCarouselPosition(animate = true) {
    if (!carouselTrack) return;

    const cardWidth = getCardWidth();
    const gap = getGap();
    const offset = currentIndex * (cardWidth + gap);

    if (animate) {
      carouselTrack.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    } else {
      carouselTrack.style.transition = 'none';
    }

    carouselTrack.style.transform = `translateX(-${offset}px)`;
  }

  function handleTransitionEnd(e) {
    if (e.target !== carouselTrack) return;
    isTransitioning = false;

    // クローン位置に達したら、実際のカードにジャンプ
    if (currentIndex >= totalCards + visibleCards) {
      // 末尾クローンに達した → 先頭の実カードへ
      currentIndex = visibleCards;
      updateCarouselPosition(false);
    } else if (currentIndex < visibleCards) {
      // 先頭クローンに達した → 末尾の実カードへ
      currentIndex = totalCards + currentIndex;
      updateCarouselPosition(false);
    }
  }

  // 前へボタン
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (totalCards <= visibleCards || isTransitioning) return;
      hideInitialImage();
      isTransitioning = true;
      currentIndex--;
      updateCarouselPosition(true);
    });
  }

  // 次へボタン
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      if (totalCards <= visibleCards || isTransitioning) return;
      hideInitialImage();
      isTransitioning = true;
      currentIndex++;
      updateCarouselPosition(true);
    });
  }

  // トランジション終了時の処理
  if (carouselTrack) {
    carouselTrack.addEventListener('transitionend', handleTransitionEnd);
  }

  // ボタンの表示/非表示
  function updateButtons() {
    if (totalCards <= visibleCards) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
    } else {
      if (prevBtn) prevBtn.style.display = '';
      if (nextBtn) nextBtn.style.display = '';
    }
  }

  // リサイズ時にカルーセルをリセット
  window.addEventListener('resize', () => {
    if (!isDesktop()) {
      carouselTrack.style.transform = '';
      carouselTrack.style.transition = '';
      currentIndex = 0;
    } else if (hasAnimated) {
      // アニメーション完了後のリサイズ時のみ再セットアップ
      setupInfiniteCarousel();
      // 初期画像オーバーレイは幅変更に追従できず静止する→完了後は畳んで本物カード（CSS追従）に任せる
      if (animationComplete) hideInitialImage();
    }
    updateButtons();
  });

  // 初期状態でボタン表示を更新
  updateButtons();
}

// ============================================
// Business Page Carousels
// ============================================
function initBusinessCarousels() {
  const businessProjects = document.querySelectorAll('.business-projects');
  if (!businessProjects.length) return;

  businessProjects.forEach((section) => {
    const carouselTrack = section.querySelector('.projects__carousel-track');
    const cards = section.querySelectorAll('.projects__card');
    const prevBtn = section.querySelector('.projects__nav-btn--prev');
    const nextBtn = section.querySelector('.projects__nav-btn--next');

    if (!carouselTrack || cards.length === 0) return;

    const visibleCards = 3;
    let currentIndex = 0;
    const totalCards = cards.length;
    let isTransitioning = false;

    const isDesktop = () => window.innerWidth >= 960;

    // カード幅を取得
    function getCardWidth() {
      if (cards.length === 0) return 0;
      return cards[0].offsetWidth;
    }

    // gap値を取得
    function getGap() {
      const style = window.getComputedStyle(carouselTrack);
      return parseFloat(style.gap) || 40;
    }

    // 無限ループ用のクローンを作成
    function setupInfiniteCarousel() {
      if (!isDesktop() || totalCards <= visibleCards) return;

      // 既存のクローンを削除
      const existingClones = carouselTrack.querySelectorAll('.is-clone');
      existingClones.forEach(clone => clone.remove());

      carouselTrack.style.transition = 'none';

      // 最後のvisibleCards枚を先頭にクローン
      const clonesBefore = [];
      for (let i = totalCards - visibleCards; i < totalCards; i++) {
        const clone = cards[i].cloneNode(true);
        clone.classList.add('is-clone');
        clonesBefore.push(clone);
      }
      for (let i = clonesBefore.length - 1; i >= 0; i--) {
        carouselTrack.insertBefore(clonesBefore[i], carouselTrack.firstChild);
      }

      // 最初のvisibleCards枚を末尾にクローン
      for (let i = 0; i < visibleCards; i++) {
        const clone = cards[i].cloneNode(true);
        clone.classList.add('is-clone');
        carouselTrack.appendChild(clone);
      }

      // 初期位置を設定
      currentIndex = clonesBefore.length;
      const cardWidth = getCardWidth();
      const gap = getGap();
      const offset = currentIndex * (cardWidth + gap);
      carouselTrack.style.transform = `translateX(-${offset}px)`;
    }

    // カルーセル位置を更新
    function updateCarouselPosition(animate = true) {
      const cardWidth = getCardWidth();
      const gap = getGap();
      const offset = currentIndex * (cardWidth + gap);

      if (animate) {
        carouselTrack.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      } else {
        carouselTrack.style.transition = 'none';
      }

      carouselTrack.style.transform = `translateX(-${offset}px)`;
    }

    // トランジション終了時の処理
    function handleTransitionEnd(e) {
      // 子要素からのバブリングイベントを無視（trackのtransform以外）
      if (e.target !== carouselTrack || e.propertyName !== 'transform') {
        return;
      }

      isTransitioning = false;

      // 無限ループが有効でない場合は何もしない
      if (totalCards <= visibleCards) return;

      // 無限ループの端に達した場合、瞬時に反対側へ移動
      if (currentIndex >= totalCards + visibleCards) {
        currentIndex = visibleCards;
        updateCarouselPosition(false);
      } else if (currentIndex < visibleCards) {
        currentIndex = totalCards + currentIndex;
        updateCarouselPosition(false);
      }
    }

    // イベントリスナー
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (totalCards <= visibleCards || isTransitioning) return;
        isTransitioning = true;
        currentIndex--;
        updateCarouselPosition(true);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (totalCards <= visibleCards || isTransitioning) return;
        isTransitioning = true;
        currentIndex++;
        updateCarouselPosition(true);
      });
    }

    carouselTrack.addEventListener('transitionend', handleTransitionEnd);

    // ボタン表示/非表示
    function updateButtons() {
      if (totalCards <= visibleCards) {
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';
      } else {
        if (prevBtn) prevBtn.style.display = '';
        if (nextBtn) nextBtn.style.display = '';
      }
    }

    // リサイズ時
    window.addEventListener('resize', () => {
      if (!isDesktop()) {
        carouselTrack.style.transform = '';
        carouselTrack.style.transition = '';
        currentIndex = 0;
      } else {
        setupInfiniteCarousel();
      }
      updateButtons();
    });

    // 初期化
    if (isDesktop() && totalCards > visibleCards) {
      setupInfiniteCarousel();
    }
    updateButtons();
  });
}

// ============================================
// Project Single Page Related Carousel
// ============================================
function initProjectRelatedCarousel() {
  const section = document.querySelector('.project-single__related-carousel');
  if (!section) return;

  const carouselTrack = section.querySelector('.projects__carousel-track');
  const cards = section.querySelectorAll('.projects__card');
  const prevBtn = section.querySelector('.projects__nav-btn--prev');
  const nextBtn = section.querySelector('.projects__nav-btn--next');

  if (!carouselTrack || cards.length === 0) return;

  const visibleCards = 3;
  let currentIndex = 0;
  const totalCards = cards.length;
  let isTransitioning = false;

  const isDesktop = () => window.innerWidth >= 960;

  // カード幅を取得
  function getCardWidth() {
    if (cards.length === 0) return 0;
    return cards[0].offsetWidth;
  }

  // gap値を取得
  function getGap() {
    const style = window.getComputedStyle(carouselTrack);
    return parseFloat(style.gap) || 40;
  }

  // 無限ループ用のクローンを作成
  function setupInfiniteCarousel() {
    if (!isDesktop() || totalCards <= visibleCards) return;

    // 既存のクローンを削除
    const existingClones = carouselTrack.querySelectorAll('.is-clone');
    existingClones.forEach(clone => clone.remove());

    carouselTrack.style.transition = 'none';

    // 最後のvisibleCards枚を先頭にクローン
    const clonesBefore = [];
    for (let i = totalCards - visibleCards; i < totalCards; i++) {
      const clone = cards[i].cloneNode(true);
      clone.classList.add('is-clone');
      clonesBefore.push(clone);
    }
    for (let i = clonesBefore.length - 1; i >= 0; i--) {
      carouselTrack.insertBefore(clonesBefore[i], carouselTrack.firstChild);
    }

    // 最初のvisibleCards枚を末尾にクローン
    for (let i = 0; i < visibleCards; i++) {
      const clone = cards[i].cloneNode(true);
      clone.classList.add('is-clone');
      carouselTrack.appendChild(clone);
    }

    // 初期位置を設定
    currentIndex = clonesBefore.length;
    const cardWidth = getCardWidth();
    const gap = getGap();
    const offset = currentIndex * (cardWidth + gap);
    carouselTrack.style.transform = `translateX(-${offset}px)`;
  }

  // カルーセル位置を更新
  function updateCarouselPosition(animate = true) {
    const cardWidth = getCardWidth();
    const gap = getGap();
    const offset = currentIndex * (cardWidth + gap);

    if (animate) {
      carouselTrack.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    } else {
      carouselTrack.style.transition = 'none';
    }

    carouselTrack.style.transform = `translateX(-${offset}px)`;
  }

  // トランジション終了時の処理（カルーセルトラック自身のtransitionのみ処理）
  function handleTransitionEnd(e) {
    if (e.target !== carouselTrack) return;
    isTransitioning = false;

    // 無限ループの端に達した場合、瞬時に反対側へ移動
    if (currentIndex >= totalCards + visibleCards) {
      currentIndex = visibleCards;
      updateCarouselPosition(false);
    } else if (currentIndex < visibleCards) {
      currentIndex = totalCards + currentIndex;
      updateCarouselPosition(false);
    }
  }

  // イベントリスナー
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (totalCards <= visibleCards || isTransitioning) return;
      isTransitioning = true;
      currentIndex--;
      updateCarouselPosition(true);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      if (totalCards <= visibleCards || isTransitioning) return;
      isTransitioning = true;
      currentIndex++;
      updateCarouselPosition(true);
    });
  }

  carouselTrack.addEventListener('transitionend', handleTransitionEnd);

  // ボタン表示/非表示
  function updateButtons() {
    if (totalCards <= visibleCards) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
    } else {
      if (prevBtn) prevBtn.style.display = '';
      if (nextBtn) nextBtn.style.display = '';
    }
  }

  // リサイズ時
  window.addEventListener('resize', () => {
    if (!isDesktop()) {
      carouselTrack.style.transform = '';
      carouselTrack.style.transition = '';
      currentIndex = 0;
    } else {
      setupInfiniteCarousel();
    }
    updateButtons();
  });

  // 初期化
  if (isDesktop() && totalCards > visibleCards) {
    setupInfiniteCarousel();
  }
  updateButtons();
}

// ============================================
// News Single Page Related Carousel
// ============================================
function initNewsRelatedCarousel() {
  const section = document.querySelector('.news-single__related-carousel');
  if (!section) return;

  const carouselTrack = section.querySelector('.projects__carousel-track');
  const cards = section.querySelectorAll('.projects__card');
  const prevBtn = section.querySelector('.projects__nav-btn--prev');
  const nextBtn = section.querySelector('.projects__nav-btn--next');

  if (!carouselTrack || cards.length === 0) return;

  const visibleCards = 3;
  let currentIndex = 0;
  const totalCards = cards.length;
  let isTransitioning = false;

  const isDesktop = () => window.innerWidth >= 960;

  // カード幅を取得
  function getCardWidth() {
    if (cards.length === 0) return 0;
    return cards[0].offsetWidth;
  }

  // gap値を取得
  function getGap() {
    const style = window.getComputedStyle(carouselTrack);
    return parseFloat(style.gap) || 40;
  }

  // 無限ループ用のクローンを作成
  function setupInfiniteCarousel() {
    if (!isDesktop() || totalCards <= visibleCards) return;

    // 既存のクローンを削除
    const existingClones = carouselTrack.querySelectorAll('.is-clone');
    existingClones.forEach(clone => clone.remove());

    carouselTrack.style.transition = 'none';

    // 最後のvisibleCards枚を先頭にクローン
    const clonesBefore = [];
    for (let i = totalCards - visibleCards; i < totalCards; i++) {
      const clone = cards[i].cloneNode(true);
      clone.classList.add('is-clone');
      clonesBefore.push(clone);
    }
    for (let i = clonesBefore.length - 1; i >= 0; i--) {
      carouselTrack.insertBefore(clonesBefore[i], carouselTrack.firstChild);
    }

    // 最初のvisibleCards枚を末尾にクローン
    for (let i = 0; i < visibleCards; i++) {
      const clone = cards[i].cloneNode(true);
      clone.classList.add('is-clone');
      carouselTrack.appendChild(clone);
    }

    // 初期位置を設定
    currentIndex = clonesBefore.length;
    const cardWidth = getCardWidth();
    const gap = getGap();
    const offset = currentIndex * (cardWidth + gap);
    carouselTrack.style.transform = `translateX(-${offset}px)`;
  }

  // カルーセル位置を更新
  function updateCarouselPosition(animate = true) {
    const cardWidth = getCardWidth();
    const gap = getGap();
    const offset = currentIndex * (cardWidth + gap);

    if (animate) {
      carouselTrack.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    } else {
      carouselTrack.style.transition = 'none';
    }

    carouselTrack.style.transform = `translateX(-${offset}px)`;
  }

  // トランジション終了時の処理（カルーセルトラック自身のtransitionのみ処理）
  function handleTransitionEnd(e) {
    if (e.target !== carouselTrack) return;
    isTransitioning = false;

    // 無限ループの端に達した場合、瞬時に反対側へ移動
    if (currentIndex >= totalCards + visibleCards) {
      currentIndex = visibleCards;
      updateCarouselPosition(false);
    } else if (currentIndex < visibleCards) {
      currentIndex = totalCards + currentIndex;
      updateCarouselPosition(false);
    }
  }

  // イベントリスナー
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (totalCards <= visibleCards || isTransitioning) return;
      isTransitioning = true;
      currentIndex--;
      updateCarouselPosition(true);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      if (totalCards <= visibleCards || isTransitioning) return;
      isTransitioning = true;
      currentIndex++;
      updateCarouselPosition(true);
    });
  }

  carouselTrack.addEventListener('transitionend', handleTransitionEnd);

  // ボタン表示/非表示
  function updateButtons() {
    if (totalCards <= visibleCards) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
    } else {
      if (prevBtn) prevBtn.style.display = '';
      if (nextBtn) nextBtn.style.display = '';
    }
  }

  // リサイズ時
  window.addEventListener('resize', () => {
    if (!isDesktop()) {
      carouselTrack.style.transform = '';
      carouselTrack.style.transition = '';
      currentIndex = 0;
    } else {
      setupInfiniteCarousel();
    }
    updateButtons();
  });

  // 初期化
  if (isDesktop() && totalCards > visibleCards) {
    setupInfiniteCarousel();
  }
  updateButtons();
}

// ============================================
// Business Page Animation (Scroll Triggered Fade-in)
// ============================================
function initBusinessPageAnimation() {
  // ビジネスページの要素を取得
  const businessHero = document.querySelector('.business-hero');
  const serviceCards = document.querySelectorAll('.business-service-card');
  const recruitBusiness = document.querySelector('.recruit--business');

  // ビジネスページでなければスキップ
  if (!businessHero && !serviceCards.length) return;

  // アニメーション対象の要素を収集
  const fadeElements = [];
  const immediateElements = []; // ページロード時に即座にアニメーション

  // ヒーローは即座にアニメーション
  if (businessHero) {
    businessHero.classList.add('business-fade-in');
    immediateElements.push({ el: businessHero, delay: 100 });
  }

  // サービスカードは右からフェードイン（CSSで制御、business-fade-inは使わない）
  // 最初のカードが画面に入ったら、1, 2, 3の順で時間差でアニメーション
  let serviceCardsTriggered = false;
  if (serviceCards.length > 0) {
    const serviceCardsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !serviceCardsTriggered) {
            serviceCardsTriggered = true;
            // 時間差で各カードをアニメーション
            serviceCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('is-visible');
              }, index * 200); // 200ms間隔
            });
            serviceCardsObserver.disconnect();
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    // 最初のカードだけ監視
    serviceCardsObserver.observe(serviceCards[0]);
  }

  // アジャイルセクション：タイトルとコンテンツを個別に
  const categoryAgile = document.querySelector('.business-category--agile');
  if (categoryAgile) {
    const agileTitle = categoryAgile.querySelector('.business-category__title');
    const agileDesc = categoryAgile.querySelector('.business-category__desc');
    const agileProjects = categoryAgile.querySelector('.business-projects');
    if (agileTitle) {
      agileTitle.classList.add('business-fade-in');
      fadeElements.push(agileTitle);
    }
    if (agileDesc) {
      agileDesc.classList.add('business-fade-in');
      fadeElements.push(agileDesc);
    }
    if (agileProjects) {
      agileProjects.classList.add('business-fade-in');
      fadeElements.push(agileProjects);
    }
  }

  // 内製化セクション：タイトルとコンテンツを個別に
  const categoryInternal = document.querySelector('.business-category--internal');
  if (categoryInternal) {
    const internalTitle = categoryInternal.querySelector('.business-category__title');
    const internalDesc = categoryInternal.querySelector('.business-category__desc');
    const internalProjects = categoryInternal.querySelector('.business-projects');
    if (internalTitle) {
      internalTitle.classList.add('business-fade-in');
      fadeElements.push(internalTitle);
    }
    if (internalDesc) {
      internalDesc.classList.add('business-fade-in');
      fadeElements.push(internalDesc);
    }
    if (internalProjects) {
      internalProjects.classList.add('business-fade-in');
      fadeElements.push(internalProjects);
    }
  }

  // ソリューションセクション：ヘッダーとコンテンツを個別に
  const solution = document.querySelector('.business-solution');
  if (solution) {
    const solutionHeader = solution.querySelector('.business-solution__header');
    const solutionContent = solution.querySelector('.business-solution__content');
    if (solutionHeader) {
      solutionHeader.classList.add('business-fade-in');
      fadeElements.push(solutionHeader);
    }
    if (solutionContent) {
      solutionContent.classList.add('business-fade-in');
      fadeElements.push(solutionContent);
    }
  }

  // 採用セクションは全体
  if (recruitBusiness) {
    recruitBusiness.classList.add('business-fade-in');
    fadeElements.push(recruitBusiness);
  }

  // ページロード時に即座にアニメーションする要素（時間差で順番に）
  immediateElements.forEach(({ el, delay, isNodeList }) => {
    setTimeout(() => {
      if (isNodeList) {
        el.forEach(node => node.classList.add('is-visible'));
      } else {
        el.classList.add('is-visible');
      }
    }, delay);
  });

  // Intersection Observerで監視（スクロールで表示される要素）
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
  });

  fadeElements.forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// Company Page Animation (Scroll Triggered Fade-in)
// ============================================
function initCompanyPageAnimation() {
  const companyHero = document.querySelector('.company-hero');
  const recruitCompany = document.querySelector('.recruit--company');

  // Companyページでなければスキップ
  if (!companyHero) return;

  const fadeElements = [];
  const immediateElements = [];

  // ヒーローは即座にアニメーション
  companyHero.classList.add('business-fade-in');
  immediateElements.push({ el: companyHero, delay: 100 });

  // ミッションセクション：ロゴとコンテンツを個別に
  const mission = document.querySelector('.company-mission');
  if (mission) {
    const missionLogo = mission.querySelector('.company-mission__logo');
    const missionContent = mission.querySelector('.company-mission__content');
    if (missionLogo) {
      missionLogo.classList.add('business-fade-in');
      immediateElements.push({ el: missionLogo, delay: 300 });
    }
    if (missionContent) {
      missionContent.classList.add('business-fade-in');
      immediateElements.push({ el: missionContent, delay: 500 });
    }
  }

  // ケーパビリティセクション：ヘッダーとカードを個別に
  const capabilities = document.querySelector('.company-capabilities');
  if (capabilities) {
    const capHeader = capabilities.querySelector('.company-capabilities__header');
    const capCards = capabilities.querySelectorAll('.capability-card');
    if (capHeader) {
      capHeader.classList.add('business-fade-in');
      fadeElements.push(capHeader);
    }
    capCards.forEach(card => {
      card.classList.add('business-fade-in');
      fadeElements.push(card);
    });
  }

  // ビジョンセクション：カード全体
  const vision = document.querySelector('.company-vision');
  if (vision) {
    const visionCard = vision.querySelector('.company-vision__card');
    if (visionCard) {
      visionCard.classList.add('business-fade-in');
      fadeElements.push(visionCard);
    }

    // ビジョンロゴのアニメーション（ロゴ自体が画面に入ったらトリガー）
    const visionLogos = vision.querySelectorAll('.vision-logo');
    if (visionLogos.length > 0) {
      const visionLogoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-animating');
            visionLogoObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 1.0, // ロゴが100%見えたらトリガー
      });
      visionLogos.forEach(logo => {
        visionLogoObserver.observe(logo);
      });
    }
  }

  // バリューセクション：ヘッダーとカードを個別に
  const value = document.querySelector('.company-value');
  if (value) {
    const valueHeader = value.querySelector('.company-value__header');
    const valueCards = value.querySelectorAll('.value-card');
    if (valueHeader) {
      valueHeader.classList.add('business-fade-in');
      fadeElements.push(valueHeader);
    }
    valueCards.forEach(card => {
      card.classList.add('business-fade-in');
      fadeElements.push(card);
    });
  }

  // CEOメッセージセクション：全体
  const ceoMessage = document.querySelector('.ceo-message');
  if (ceoMessage) {
    ceoMessage.classList.add('business-fade-in');
    fadeElements.push(ceoMessage);
  }

  // 会社概要セクション：ヘッダーとテーブルを個別に
  const profile = document.querySelector('.company-profile');
  if (profile) {
    const profileHeader = profile.querySelector('.company-profile__header');
    const profileTable = profile.querySelector('.company-profile__table');
    if (profileHeader) {
      profileHeader.classList.add('business-fade-in');
      fadeElements.push(profileHeader);
    }
    if (profileTable) {
      profileTable.classList.add('business-fade-in');
      fadeElements.push(profileTable);
    }
  }

  // 採用セクションは全体
  if (recruitCompany) {
    recruitCompany.classList.add('business-fade-in');
    fadeElements.push(recruitCompany);
  }

  // ページロード時に即座にアニメーションする要素
  immediateElements.forEach(({ el, delay }) => {
    setTimeout(() => {
      el.classList.add('is-visible');
    }, delay);
  });

  // Intersection Observerで監視
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
  });

  fadeElements.forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// News Section Animation (Scroll Triggered)
// ============================================
function initNewsAnimation() {
  const news = document.querySelector('.fp-news');
  if (!news) return;

  const title = news.querySelector('.fp-news__title');
  const items = news.querySelectorAll('.fp-news__item');
  const footer = news.querySelector('.fp-news__footer');

  let hasAnimated = false;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !hasAnimated) {
        hasAnimated = true;
        startNewsAnimation();
      }
    });
  }, {
    threshold: 0.2,
  });

  observer.observe(news);

  function startNewsAnimation() {
    // タイトルをフェードイン
    if (title) {
      title.classList.add('is-visible');
    }

    // アイテムを順番にフェードイン
    items.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('is-visible');
      }, 200 + (index * 100));
    });

    // フッターをフェードイン
    setTimeout(() => {
      if (footer) {
        footer.classList.add('is-visible');
      }
    }, 200 + (items.length * 100) + 100);
  }
}

// ============================================
// Blog Section Animation (Scroll Triggered)
// ============================================
function initBlogAnimation() {
  const blog = document.querySelector('.fp-blog');
  if (!blog) return;

  const title = blog.querySelector('.fp-blog__title');
  const items = blog.querySelectorAll('.fp-blog__item');
  const footer = blog.querySelector('.fp-blog__footer');

  let hasAnimated = false;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !hasAnimated) {
        hasAnimated = true;
        startBlogAnimation();
      }
    });
  }, {
    threshold: 0.2,
  });

  observer.observe(blog);

  function startBlogAnimation() {
    // タイトルをフェードイン
    if (title) {
      title.classList.add('is-visible');
    }

    // アイテムを順番にフェードイン
    items.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('is-visible');
      }, 200 + (index * 100));
    });

    // フッターをフェードイン
    setTimeout(() => {
      if (footer) {
        footer.classList.add('is-visible');
      }
    }, 200 + (items.length * 100) + 100);
  }
}

// ============================================
// Recruit Section Animation (Scroll Triggered)
// ============================================
function initRecruitAnimation() {
  const recruit = document.querySelector('.recruit');
  if (!recruit) return;

  let hasAnimated = false;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !hasAnimated) {
        hasAnimated = true;
        recruit.classList.add('is-visible');
      }
    });
  }, {
    threshold: 0.2,
  });

  observer.observe(recruit);
}

// ============================================
// Careers Page Animation (Scroll Triggered Fade-in)
// ============================================
function initCareersPageAnimation() {
  const careersHero = document.querySelector('.careers-hero');

  // Careersページでなければスキップ
  if (!careersHero) return;

  const fadeElements = [];
  const immediateElements = [];

  // ヒーローは即座にアニメーション
  immediateElements.push({ el: careersHero, delay: 100 });

  // その他のセクションはスクロールで表示
  const careersJoin = document.querySelector('.careers-join');
  const careersMv = document.querySelector('.careers-mv');
  const careersValue = document.querySelector('.careers-value');
  const careersInterviews = document.querySelector('.careers-interviews');
  const careersJobs = document.querySelector('.careers-jobs');
  const careersCasual = document.querySelector('.careers-casual');

  if (careersJoin) fadeElements.push(careersJoin);
  if (careersMv) fadeElements.push(careersMv);
  if (careersValue) fadeElements.push(careersValue);
  if (careersInterviews) fadeElements.push(careersInterviews);
  if (careersJobs) fadeElements.push(careersJobs);
  if (careersCasual) fadeElements.push(careersCasual);

  // ページロード時に即座にアニメーションする要素
  immediateElements.forEach(({ el, delay }) => {
    setTimeout(() => {
      el.classList.add('is-visible');
    }, delay);
  });

  // Intersection Observerで監視（スクロールで表示される要素）
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
  });

  fadeElements.forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// Team Modal (チームページ用モーダル)
// ============================================
function initTeamModal() {
  const modal = document.getElementById('teamModal');
  if (!modal) return;

  const memberCards = document.querySelectorAll('.team-members__card');
  const backdrop = modal.querySelector('.team-modal__backdrop');
  const closeBtn = modal.querySelector('.team-modal__close');

  // モーダル要素
  const photoImg = modal.querySelector('.team-modal__photo-img');
  const nameJa = modal.querySelector('.team-modal__name-ja');
  const nameEn = modal.querySelector('.team-modal__name-en');
  const role = modal.querySelector('.team-modal__role');
  const blogLink = modal.querySelector('.team-modal__blog');
  const profile = modal.querySelector('.team-modal__profile');
  const modalContent = modal.querySelector('.team-modal__content');

  // カードクリックでモーダルを開く
  memberCards.forEach(card => {
    card.addEventListener('click', () => {
      const dataScript = card.querySelector('.team-members__data');
      if (!dataScript) return;

      try {
        const data = JSON.parse(dataScript.textContent);
        openTeamModal(data);
      } catch (e) {
        console.error('Failed to parse member data:', e);
      }
    });
  });

  // モーダルを開く
  function openTeamModal(data) {
    if (photoImg) photoImg.src = data.thumbnail || '';

    // 言語に応じた表示（PHPで選択済みのデータを使用）
    if (nameJa) nameJa.textContent = data.name_ja || '';
    if (nameEn) nameEn.textContent = data.name_en || '';
    if (role) role.textContent = data.position || '';

    // ブログリンク（管理画面で表示ON＋URLありのメンバーのみ）
    if (blogLink) {
      if (data.blog_url) {
        blogLink.href = data.blog_url;
        blogLink.hidden = false;
      } else {
        blogLink.hidden = true;
        blogLink.removeAttribute('href');
      }
    }

    // プロフィール（改行を段落に変換）
    const profileText = data.profile || '';
    if (profile && profileText) {
      const paragraphs = profileText.split(/\n\n+/).filter(p => p.trim());
      profile.innerHTML = paragraphs.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
    } else if (profile) {
      profile.innerHTML = '';
    }

    // スクロール位置をリセット
    if (modalContent) {
      modalContent.scrollTop = 0;
    }

    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('team-modal-open');
  }

  // モーダルを閉じる
  function closeTeamModal() {
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('team-modal-open');
  }

  // 閉じるボタン
  if (closeBtn) {
    closeBtn.addEventListener('click', closeTeamModal);
  }

  // 背景クリック
  if (backdrop) {
    backdrop.addEventListener('click', closeTeamModal);
  }

  // ESCキー
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeTeamModal();
    }
  });
}

// ============================================
// Team Page Animation (Scroll Triggered Fade-in)
// ============================================
function initTeamPageAnimation() {
  const teamHero = document.querySelector('.team-hero');
  const memberCards = document.querySelectorAll('.team-members__card');

  // Teamページでなければスキップ
  if (!teamHero) return;

  const immediateElements = [];
  const fadeElements = [];

  // ヒーローは即座にアニメーション
  immediateElements.push({ el: teamHero, delay: 100 });

  // ビューポート内かどうかを判定
  const isInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  // ビューポート内のカードを収集
  const visibleCards = [];
  memberCards.forEach((card) => {
    if (isInViewport(card)) {
      visibleCards.push(card);
    } else {
      fadeElements.push(card);
    }
  });

  // ビューポート内のカードを行ごとにグループ化
  const rows = [];
  let currentRow = [];
  let currentTop = null;
  const rowThreshold = 10;

  visibleCards.forEach((card) => {
    const rect = card.getBoundingClientRect();
    if (currentTop === null || Math.abs(rect.top - currentTop) <= rowThreshold) {
      currentRow.push(card);
      if (currentTop === null) currentTop = rect.top;
    } else {
      rows.push(currentRow);
      currentRow = [card];
      currentTop = rect.top;
    }
  });
  if (currentRow.length > 0) {
    rows.push(currentRow);
  }

  // 行ごとにアニメーション（同じ行のカードは同時に表示）
  rows.forEach((row, rowIndex) => {
    row.forEach((card) => {
      immediateElements.push({ el: card, delay: 300 + (rowIndex * 150) });
    });
  });

  // ページロード時に即座にアニメーションする要素
  immediateElements.forEach(({ el, delay }) => {
    setTimeout(() => {
      el.classList.add('is-visible');
    }, delay);
  });

  // Intersection Observerで監視（ビューポート外のカード用）
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
  });

  fadeElements.forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// News Page Animation (Scroll Triggered Fade-in)
// ============================================
function initNewsPageAnimation() {
  const pageNews = document.querySelector('.page-news');
  if (!pageNews) return;

  const fadeElements = [];
  const immediateElements = [];

  // ビューポート内かどうかを判定
  const isInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  // タイトルセクション（CSSで初期状態設定済み）
  const titleSection = pageNews.querySelector('.page-title-section');
  if (titleSection) {
    immediateElements.push({ el: titleSection, delay: 100 });
  }

  // カテゴリフィルター（CSSで初期状態設定済み）
  const categoryFilter = pageNews.querySelector('.category-filter');
  if (categoryFilter) {
    immediateElements.push({ el: categoryFilter, delay: 300 });
  }

  // ニュースカード（ビューポート内のものは1つずつ順番にアニメーション）
  const newsCards = pageNews.querySelectorAll('.news-card');
  let visibleIndex = 0;
  newsCards.forEach((card) => {
    if (isInViewport(card)) {
      // ビューポート内：時間差でアニメーション
      immediateElements.push({ el: card, delay: 400 + (visibleIndex * 150) });
      visibleIndex++;
    } else {
      // ビューポート外：スクロールで表示
      card.classList.add('news-fade-in');
      fadeElements.push(card);
    }
  });

  // ページネーション
  const pagination = pageNews.querySelector('.pagination');
  if (pagination) {
    pagination.classList.add('news-fade-in');
    fadeElements.push(pagination);
  }

  // ページロード時に即座にアニメーションする要素
  immediateElements.forEach(({ el, delay }) => {
    setTimeout(() => {
      el.classList.add('is-visible');
    }, delay);
  });

  // Intersection Observerで監視
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
  });

  fadeElements.forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// Projects Page Animation (Scroll Triggered Fade-in)
// ============================================
function initProjectsPageAnimation() {
  const pageProjects = document.querySelector('.page-projects');
  if (!pageProjects) return;

  const fadeElements = [];
  const immediateElements = [];

  // ビューポート内かどうかを判定
  const isInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  // タイトルセクション（CSSで初期状態設定済み）
  const titleSection = pageProjects.querySelector('.page-title-section');
  if (titleSection) {
    immediateElements.push({ el: titleSection, delay: 100 });
  }

  // プロジェクトカード（ビューポート内のものは1つずつ順番にアニメーション）
  const projectCards = pageProjects.querySelectorAll('.project-card');
  let visibleIndex = 0;
  projectCards.forEach((card) => {
    if (isInViewport(card)) {
      // ビューポート内：時間差でアニメーション
      immediateElements.push({ el: card, delay: 300 + (visibleIndex * 150) });
      visibleIndex++;
    } else {
      // ビューポート外：スクロールで表示
      card.classList.add('projects-fade-in');
      fadeElements.push(card);
    }
  });

  // ページネーション
  const pagination = pageProjects.querySelector('.pagination');
  if (pagination) {
    pagination.classList.add('projects-fade-in');
    fadeElements.push(pagination);
  }

  // ページロード時に即座にアニメーションする要素
  immediateElements.forEach(({ el, delay }) => {
    setTimeout(() => {
      el.classList.add('is-visible');
    }, delay);
  });

  // Intersection Observerで監視
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
  });

  fadeElements.forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// Interview Page Animation (Scroll Triggered Fade-in)
// ============================================
function initInterviewPageAnimation() {
  const interview = document.querySelector('.interview');
  if (!interview) return;

  const fadeElements = [];
  const immediateElements = [];

  // ヘッダー（タイトル）
  const header = interview.querySelector('.interview__header');
  if (header) {
    immediateElements.push({ el: header, delay: 100 });
  }

  // ヒーロー画像
  const hero = interview.querySelector('.interview__hero');
  if (hero) {
    immediateElements.push({ el: hero, delay: 300 });
  }

  // プロフィールセクション
  const profile = interview.querySelector('.interview__profile');
  if (profile) {
    immediateElements.push({ el: profile, delay: 500 });
  }

  // コンテンツ（スクロールで表示）
  const content = interview.querySelector('.interview__content');
  if (content) {
    fadeElements.push(content);
  }

  // 応募ボタン（スクロールで表示）
  const apply = interview.querySelector('.interview__apply');
  if (apply) {
    fadeElements.push(apply);
  }

  // 関連インタビュー（スクロールで表示）
  const relatedSection = document.querySelector('.interview + .careers-interviews');
  if (relatedSection) {
    fadeElements.push(relatedSection);
  }

  // ページロード時に即座にアニメーションする要素
  immediateElements.forEach(({ el, delay }) => {
    setTimeout(() => {
      el.classList.add('is-visible');
    }, delay);
  });

  // Intersection Observerで監視（クラスベース）
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.05,
    rootMargin: '0px 0px 0px 0px',
  });

  fadeElements.forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// Policy Page Animation (Scroll Triggered Fade-in)
// ============================================
function initPolicyPageAnimation() {
  const pagePolicy = document.querySelector('.page-policy');
  if (!pagePolicy) return;

  // タイトルセクション
  const titleSection = pagePolicy.querySelector('.page-title-section');
  if (titleSection) {
    setTimeout(() => {
      titleSection.classList.add('is-visible');
    }, 100);
  }

  // コンテンツ
  const content = pagePolicy.querySelector('.policy-content');
  if (content) {
    setTimeout(() => {
      content.classList.add('is-visible');
    }, 300);
  }
}

// ============================================
// Contact Form (MW WP Form Integration)
// ============================================
function initContactForm() {
  const pageContact = document.querySelector('.page-contact');
  if (!pageContact) return;

  const mwForm = pageContact.querySelector('.mw_wp_form');
  const customSubmitBtn = document.getElementById('custom-submit-btn');
  const customBackBtn = document.getElementById('custom-back-btn');

  // MW WP Formがない場合は終了
  if (!mwForm) return;

  // フォーム要素にaria-labelを付与（a11y対応）
  mwForm.querySelectorAll('input, textarea, select').forEach((el) => {
    if (el.type === 'hidden' || el.type === 'submit' || el.getAttribute('aria-label')) return;
    const name = el.getAttribute('name') || '';
    const placeholder = el.getAttribute('placeholder') || '';
    const closestLabel = el.closest('.mwform-checkbox-field, .mwform-radio-field');
    if (closestLabel) return;
    const label = placeholder || name.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    if (label) el.setAttribute('aria-label', label);
  });

  // MW WP Formのボタンを取得（日本語・英語両対応）
  const mwSubmitBtn = mwForm.querySelector('input[type="submit"][value*="確認"]') ||
                      mwForm.querySelector('input[type="submit"][value*="Confirm"]') ||
                      mwForm.querySelector('input[type="submit"][value*="Review"]') ||
                      mwForm.querySelector('input[type="submit"][name="submitConfirm"]');
  const mwConfirmBtn = mwForm.querySelector('input[type="submit"][name="confirm_button"]') ||
                       mwForm.querySelector('input[type="submit"][value*="送信"]') ||
                       mwForm.querySelector('input[type="submit"][value*="Submit"]') ||
                       mwForm.querySelector('input[type="submit"][value*="Send"]');
  const mwBackBtn = mwForm.querySelector('input[type="submit"][name="submitBack"]') ||
                    mwForm.querySelector('input[type="submit"][value*="戻る"]') ||
                    mwForm.querySelector('input[type="submit"][value*="Back"]');

  // 完了画面の場合、ボタンをTOPリンクに変更
  if (mwForm.classList.contains('mw_wp_form_complete') && customSubmitBtn) {
    customSubmitBtn.href = '/';
    customSubmitBtn.removeAttribute('id');
    return;
  }

  // 確認画面の場合、ラベルと値をspan化して横並びにする
  if (mwForm.classList.contains('mw_wp_form_preview')) {
    formatPreviewFields(mwForm);
  }

  // カスタム送信ボタンのクリック処理
  if (customSubmitBtn) {
    customSubmitBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // 入力画面の場合
      if (mwSubmitBtn && !mwForm.classList.contains('mw_wp_form_preview')) {
        mwSubmitBtn.click();
        return;
      }

      // 確認画面の場合
      if (mwConfirmBtn && mwForm.classList.contains('mw_wp_form_preview')) {
        mwConfirmBtn.click();
        return;
      }
    });
  }

  // 戻るボタンのクリック処理（MW WP Formの戻るボタンがない場合はhistory.back()）
  if (customBackBtn) {
    customBackBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (mwBackBtn) {
        mwBackBtn.click();
      } else {
        // MW WP Formの戻るボタンがない場合はブラウザの履歴で戻る
        history.back();
      }
    });
  }
}

// 確認画面のフィールドをフォーマット（ラベルと値を分離）
function formatPreviewFields(mwForm) {
  const form = mwForm.querySelector('form');
  if (!form) return;

  // フォーム直下のpタグとprivacy-section内のpタグを処理
  const pTags = form.querySelectorAll(':scope > p, .privacy-section > p:not(.privacy-title):not(.privacy-text)');

  pTags.forEach(p => {
    // 送信ボタンを含むpはスキップ
    if (p.querySelector('input[type="submit"]')) return;
    // 空のpはスキップ
    if (!p.textContent.trim()) return;
    // privacy-titleとprivacy-textはスキップ
    if (p.classList.contains('privacy-title') || p.classList.contains('privacy-text')) return;

    // brタグを探す
    const br = p.querySelector('br');
    if (!br) return;

    // brの前後でコンテンツを分割
    let labelText = '';
    let valueText = '';

    // brより前のテキストを取得（ラベル）
    let currentNode = p.firstChild;
    while (currentNode && currentNode !== br) {
      if (currentNode.nodeType === 3) { // テキストノード
        labelText += currentNode.textContent;
      } else if (currentNode.nodeType === 1 && currentNode.classList && currentNode.classList.contains('required-mark')) {
        // required-markはスキップ
      } else if (currentNode.nodeType === 1) {
        labelText += currentNode.textContent;
      }
      currentNode = currentNode.nextSibling;
    }
    labelText = labelText.replace(/\*/g, '').trim();

    // brより後のテキストを取得（値）
    if (br.nextSibling) {
      currentNode = br.nextSibling;
      while (currentNode) {
        if (currentNode.nodeType === 3) { // テキストノード
          valueText += currentNode.textContent;
        } else if (currentNode.nodeType === 1 && currentNode.tagName !== 'INPUT') {
          valueText += currentNode.textContent;
        }
        currentNode = currentNode.nextSibling;
      }
    }
    valueText = valueText.trim();

    // pの内容を再構築
    if (labelText && valueText) {
      // hidden inputを保持
      const hiddenInputs = p.querySelectorAll('input[type="hidden"]');
      const hiddenHtml = Array.from(hiddenInputs).map(i => i.outerHTML).join('');

      p.innerHTML = `<span class="preview-label">${labelText}</span><span class="preview-value">${valueText}</span>${hiddenHtml}`;
    }
  });
}
